// MEN-5598: a scheduling assistant has no Mento account. Their session is an
// opaque Moments token kept per tab in sessionStorage, under its own key, and
// only the assistant API ever reads it. Member credentials (Rails cookies, the
// Moments member session) never reach the assistant routes, and this token
// never reaches any other route.

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'mento.schedulingAssistant.session';

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

export interface AssistantSession {
  expiresAt: string;
  token: string;
}

const storage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const removeStored = () => {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Storage can be unavailable (private mode); nothing to clear then.
  }
};

export const clearAssistantSession = (): void => {
  removeStored();
  notify();
};

// Expiry is left to Moments: an expired token gets a 401, and the page then
// tells the assistant their link expired instead of silently signing them out.
export const readAssistantSession = (): AssistantSession | null => {
  let raw: string | null = null;
  try {
    raw = storage()?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AssistantSession>;
    if (typeof parsed.token !== 'string' || !parsed.token) {
      removeStored();
      return null;
    }
    return { expiresAt: parsed.expiresAt ?? '', token: parsed.token };
  } catch {
    removeStored();
    return null;
  }
};

export const writeAssistantSession = (session: AssistantSession): void => {
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Without storage there is no session; the pages ask for a new link.
  }
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const readToken = () => readAssistantSession()?.token ?? null;

/** The assistant session token in this tab, re-rendering when it changes. */
export const useAssistantSessionToken = (): string | null =>
  useSyncExternalStore(subscribe, readToken, () => null);
